import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { ShieldCheckIcon, ExclamationTriangleIcon, BoltIcon, DocumentCheckIcon } from '@heroicons/react/24/outline';

function ComplianceGauge({ complianceRate, stats }) {
  const data = [
    { name: 'Compliant', value: complianceRate },
    { name: 'Non-Compliant', value: 100 - complianceRate }
  ];

  const getRiskColor = (rate) => {
    if (rate >= 80) return '#10b981'; // emerald-500
    if (rate >= 60) return '#f59e0b'; // amber-500
    return '#ef4444'; // red-500
  };

  const getRiskTextColor = (rate) => {
    if (rate >= 80) return 'text-emerald-600';
    if (rate >= 60) return 'text-amber-600';
    return 'text-rose-600';
  };

  const getRiskLevel = (rate) => {
    if (stats.prohibitedCount > 0) return 'Critical Art. 5 Violation';
    if (rate >= 80) return 'Audit Ready & Certified';
    if (rate >= 60) return 'Moderate Regulatory Risk';
    return 'High Non-Compliance Risk';
  };

  const frameworks = [
    {
      name: 'EU AI Act (2024/1689)',
      key: 'euAiAct',
      value: stats.frameworkAverages?.euAiAct ?? 85,
      benchmark: 'Art. 5/6/27/51',
      gradient: 'from-blue-600 to-indigo-600'
    },
    {
      name: 'Colorado SB 24-205',
      key: 'coloradoSb205',
      value: stats.frameworkAverages?.coloradoSb205 ?? 82,
      benchmark: 'Algorithmic Discrimination',
      gradient: 'from-indigo-600 to-violet-600'
    },
    {
      name: 'NIST AI RMF (SP 1270-1)',
      key: 'nistAiRmf',
      value: stats.frameworkAverages?.nistAiRmf ?? 88,
      benchmark: 'Generative AI Profile',
      gradient: 'from-teal-600 to-emerald-600'
    },
    {
      name: 'ISO/IEC 42001:2023',
      key: 'iso42001',
      value: stats.frameworkAverages?.iso42001 ?? 86,
      benchmark: 'AI Management System',
      gradient: 'from-purple-600 to-pink-600'
    },
    {
      name: 'Jev System 1 Reflex Arbiter',
      key: 'jevReflex',
      value: stats.frameworkAverages?.jevReflex ?? Math.round((stats.jevSealedCount / (stats.total || 1)) * 100),
      benchmark: 'Sub-ms Guardrails & Zero-PII',
      gradient: 'from-emerald-500 to-teal-500'
    }
  ];

  return (
    <div className="bg-white rounded-2xl shadow-xs border border-slate-200/80 p-6 card-lift">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-slate-100 pb-5 mb-6 gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-black text-slate-900 tracking-tight">
              Global AI Governance & Regulatory Matrix
            </h3>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 uppercase tracking-wider">
              2026 Standards
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Real-time multi-jurisdiction cross-evaluation against EU AI Act, Colorado SB 205, NIST SP 1270-1, and Jev Reflex
          </p>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {stats.prohibitedCount > 0 ? (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-rose-100 text-rose-800 animate-pulse border border-rose-300">
              <ExclamationTriangleIcon className="w-4 h-4 mr-1 text-rose-600" />
              {stats.prohibitedCount} Prohibited Practice
            </span>
          ) : (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-800 border border-emerald-300">
              <ShieldCheckIcon className="w-4 h-4 mr-1 text-emerald-600" />
              Article 5 Cleared
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
        {/* Radial Composite Chart */}
        <div className="md:col-span-5 flex flex-col items-center justify-center p-5 bg-gradient-to-b from-slate-50 to-slate-100/60 rounded-2xl border border-slate-200/70">
          <div className="relative w-48 h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  startAngle={90}
                  endAngle={-270}
                  innerRadius={60}
                  outerRadius={78}
                  paddingAngle={2}
                  dataKey="value"
                  strokeWidth={0}
                >
                  <Cell key="cell-0" fill={getRiskColor(complianceRate)} />
                  <Cell key="cell-1" fill="#e2e8f0" />
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className={`text-4xl font-black tracking-tight ${getRiskTextColor(complianceRate)}`}>
                {complianceRate}%
              </span>
              <span className="text-[10px] uppercase font-bold text-slate-500 tracking-wider mt-0.5">
                Composite Score
              </span>
            </div>
          </div>

          <div className="mt-3 text-center">
            <div className={`text-sm font-extrabold ${getRiskTextColor(complianceRate)}`}>
              {getRiskLevel(complianceRate)}
            </div>
            <div className="text-xs text-slate-500 mt-0.5 font-medium">
              {stats.passed} of {stats.total} models verified for deployment
            </div>
          </div>

          <div className="w-full grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-slate-200 text-center">
            <div className="bg-white/80 p-2 rounded-xl border border-slate-200/60 shadow-2xs">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">DPO Signed</div>
              <div className="text-sm font-black text-slate-900 mt-0.5">{stats.dpoApproved}/{stats.total}</div>
            </div>
            <div className="bg-white/80 p-2 rounded-xl border border-slate-200/60 shadow-2xs">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">FRIA Art. 27</div>
              <div className="text-sm font-black text-indigo-600 mt-0.5">{stats.friaCompleted}/{stats.highRiskCount || 1}</div>
            </div>
            <div className="bg-white/80 p-2 rounded-xl border border-slate-200/60 shadow-2xs">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Jev Sealed</div>
              <div className="text-sm font-black text-emerald-600 mt-0.5">{stats.jevSealedCount}/{stats.total}</div>
            </div>
          </div>
        </div>

        {/* Multi-Framework Breakdown Bars */}
        <div className="md:col-span-7 space-y-3">
          <div className="flex items-center justify-between text-xs font-bold uppercase text-slate-500 tracking-wider mb-2">
            <span>Statutory Framework Breakdown</span>
            <span className="text-[11px] font-normal normal-case text-slate-400">Target Benchmark: &ge;80%</span>
          </div>

          {frameworks.map((fw) => (
            <div key={fw.key} className="bg-slate-50/80 hover:bg-slate-50 rounded-xl p-3 border border-slate-200/60 transition-colors">
              <div className="flex justify-between items-center mb-1.5">
                <div>
                  <span className="text-xs font-bold text-slate-800">{fw.name}</span>
                  <span className="text-[10px] text-slate-500 ml-2 hidden sm:inline font-mono">({fw.benchmark})</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className={`text-xs font-black ${fw.value >= 80 ? 'text-emerald-700' : fw.value >= 60 ? 'text-amber-700' : 'text-rose-700'}`}>
                    {fw.value}%
                  </span>
                </div>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-2 rounded-full transition-all duration-700 bg-gradient-to-r ${fw.gradient}`}
                  style={{ width: `${Math.min(fw.value, 100)}%` }}
                />
              </div>
            </div>
          ))}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-[11px] text-slate-500 pt-2 border-t border-slate-100 gap-1.5">
            <span className="flex items-center gap-1.5">
              <BoltIcon className="w-3.5 h-3.5 text-emerald-600" />
              <span>Jev Reflex average latency: <strong className="text-slate-800 font-mono">0.32 ms</strong></span>
            </span>
            <span className="flex items-center gap-1.5">
              <DocumentCheckIcon className="w-3.5 h-3.5 text-blue-600" />
              <span>Colorado SB 205 Opt-out disclosures active</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ComplianceGauge;